import React, { useEffect, useState } from "react";
import axios from "axios";
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
    ResponsiveContainer, Cell, PieChart, Pie
} from "recharts";
import {
    CreditCard as CreditCardIcon, DollarSign as DollarSignIcon,
    Wallet, CreditCard, PieChart as PieChartIcon
} from 'lucide-react';

const Payments = ({ isMobile, colors }) => {

    /* ================= STATE ================= */

    const [generatePaymentModeData, setGeneratePaymentModeData] = useState({
        summary: [],
        feeAnalysis: {
            totalFees: 0,
            avgFeePercentage: 0
        }
    });

    const [loading, setLoading] = useState(false);

    /* ================= HELPERS ================= */

    const formatRupees = (amount) =>
        `₹${Number(amount || 0).toLocaleString('en-IN')}`;

    /* ================= API CALL ================= */

    useEffect(() => {
        const fetchOrders = async () => {
            const vendorId = localStorage.getItem("vendorId");
            const token = localStorage.getItem("authToken");

            if (!vendorId || !token) return;

            const toDate = new Date().toISOString().split("T")[0];
            const fromDate = new Date(
                Date.now() - 7 * 24 * 60 * 60 * 1000
            ).toISOString().split("T")[0];

            setLoading(true);

            try {
                const res = await axios.get(
                    "https://backend.maamaas.com/food/api/orders/vendor/statistics/custom",
                    {
                        params: { vendorId, fromDate, toDate },
                        headers: { Authorization: `Bearer ${token}` }
                    }
                );

                const api = res.data?.data || res.data || {};

                const paymentBreakdown = api.paymentBreakdown || {};
                const totalOrders = Number(api.totalOrders || 0);
                const totalRevenue = Number(api.totalRevenue || 0);

                const colorsMap = {
                    Cash: "#7c3aed",
                    Online_Payment: "#10b981",
                    Card: "#f59e0b",
                    Wallet: "#ef4444"
                };

                const summary = Object.entries(paymentBreakdown).map(
                    ([mode, amount], index) => ({
                        name: mode.replace("_", " "),
                        amount: Number(amount),
                        transactions: totalRevenue
                            ? Math.round((amount / totalRevenue) * totalOrders)
                            : 0,
                        processingFee: 0,
                        fee: 0,
                        color: colorsMap[mode] || colors.primary
                    })
                );

                setGeneratePaymentModeData({
                    summary,
                    feeAnalysis: {
                        totalFees: 0,
                        avgFeePercentage: 0
                    }
                });

            } catch (error) {
                console.error("❌ Payments API error:", error);
                setGeneratePaymentModeData({
                    summary: [],
                    feeAnalysis: { totalFees: 0, avgFeePercentage: 0 }
                });
            } finally {
                setLoading(false);
            }
        };

        fetchOrders();
    }, []);


    /* ================= PAYMENT MODE ANALYSIS ================= */

    const PaymentModeAnalysis = () => {
        const paymentData = generatePaymentModeData;

        return (
            <div style={{
                background: 'white',
                borderRadius: '24px',
                padding: isMobile ? '20px' : '28px',
                marginBottom: isMobile ? '20px' : '28px',
                border: '1px solid #e2e8f0',
                boxShadow: '0 12px 32px rgba(0,0,0,0.05)',
                position: 'relative',
                overflow: 'hidden'
            }}>
                <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    background: 'linear-gradient(90deg, #7c3aed, #3b82f6)'
                }} />

                <div style={{ height: isMobile ? '300px' : '380px' }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={paymentData.summary}>
                            <defs>
                                {paymentData.summary.map((e, i) => (
                                    <linearGradient key={i} id={`colorPayment${i}`} x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor={e.color} stopOpacity={0.8} />
                                        <stop offset="95%" stopColor={e.color} stopOpacity={0.2} />
                                    </linearGradient>
                                ))}
                            </defs>

                            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                            <XAxis dataKey="name" axisLine={false} tickLine={false} />
                            <YAxis tickFormatter={formatRupees} axisLine={false} tickLine={false} />

                            <Tooltip formatter={(v) => formatRupees(v)} />

                            <Bar dataKey="amount" radius={[8, 8, 0, 0]}>
                                {paymentData.summary.map((e, i) => (
                                    <Cell key={i} fill={`url(#colorPayment${i})`} />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>
        );
    };

    /* ================= SUMMARY CARDS ================= */

    const PaymentsSummaryCards = () => {
        const totalTransactions = generatePaymentModeData.summary.reduce(
            (sum, m) => sum + m.transactions,
            0
        );

        const cards = [
            { title: "Transactions", value: totalTransactions, icon: <CreditCardIcon />, color: "#7c3aed" },
            { title: "Cash", value: Math.round(totalTransactions * 0.25), icon: <DollarSignIcon />, color: "#ef4444" },
            { title: "Online", value: Math.round(totalTransactions * 0.15), icon: <CreditCard />, color: "#10b981" },
            { title: "Wallet", value: Math.round(totalTransactions * 0.1), icon: <Wallet />, color: "#f59e0b" }
        ];

        return (
            <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(4,1fr)',
                gap: '12px',
                marginBottom: '20px'
            }}>
                {cards.map((c, i) => (
                    <div key={i} style={{
                        background: '#f8fafc',
                        borderRadius: '12px',
                        padding: '16px',
                        textAlign: 'center'
                    }}>
                        <div style={{ color: c.color }}>{c.icon}</div>
                        <div style={{ fontWeight: 800 }}>{c.value.toLocaleString('en-IN')}</div>
                        <div>{c.title}</div>
                    </div>
                ))}
            </div>
        );
    };

    /* ================= FEE DISTRIBUTION ================= */

    const FeeDistributionChart = () => (
        <div style={{ background: "white", padding: "28px", borderRadius: "24px" }}>
            <div style={{ height: "350px" }}>
                <ResponsiveContainer>
                    <PieChart>
                        <Pie
                            data={generatePaymentModeData.summary}
                            dataKey="amount"
                            outerRadius={120}
                            label={(e) => `${e.name}: ${formatRupees(e.amount)}`}
                        >
                            {generatePaymentModeData.summary.map((e, i) => (
                                <Cell key={i} fill={e.color} />
                            ))}
                        </Pie>
                        <Tooltip formatter={(v) => formatRupees(v)} />
                    </PieChart>
                </ResponsiveContainer>
            </div>
        </div>
    );

    /* ================= RENDER ================= */

    return (
        <>
            <PaymentsSummaryCards />
            <PaymentModeAnalysis />
            <FeeDistributionChart />
        </>
    );
};

export default Payments;
